import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './en.json';
import zhTW from './zh-TW.json';

/**
 * ARCHITECTURE §10.8：zh-TW 是**源語言與 fallback**，en 是真正的第二語系（美股就這樣讀）。
 *
 * 刻意不放進 i18n bundle 的東西有兩類：
 *  1. **金融名詞標籤**——來自 meta.metric_registry 的 label_zh / label_en。
 *     一個 metric 只有一個被命名的地方；雙寫必然漂移。
 *  2. **§15.4 的法定揭露文字**——見 src/lib/compliance/disclaimers.ts。
 *     i18n 查不到 key 會回傳 key 本身，那等於一個沒有揭露的模型輸出。
 *
 * 會放進來的是「模型分位」這類**框架語句**，因為 §15.3 第三層要求
 * content lint 掃 i18n bundle——翻譯者把「位於第 91 百分位」改寫成
 * §15.2 對照表左欄那種方向性預測時，必須在 CI 擋下來。
 */
export const SUPPORTED_LANGUAGES = ['zh-TW', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

const STORAGE_KEY = 'stock.lang';

function readInitialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'zh-TW' || stored === 'en') {
      return stored;
    }
  } catch {
    // 讀不到就走預設。
  }
  return 'zh-TW';
}

export function persistLanguage(lang: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // 記不住語言偏好不是致命錯誤。
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    'zh-TW': { translation: zhTW },
    en: { translation: en },
  },
  lng: readInitialLanguage(),
  fallbackLng: 'zh-TW',
  supportedLngs: SUPPORTED_LANGUAGES,
  interpolation: {
    // React 已經逸出過了，再逸出一次會把全形括號變成 HTML entity。
    escapeValue: false,
  },
});

export default i18n;
